import * as React from "react";
import { Modal, Form, Button } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";

import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";

import { EppDecision, EppDecisions, Covid19VaccinationStatus, Covid19VaccinationStatuses } from "../model";
import { MARK_OH_CASE_IMMUNISATION_CLEAR } from "../../immunisationOhCase/actionTypes";

import { ImmunisationWarningModal } from "./ImmunisationWarningModal";

interface Props {
    onSubmit: (
        eppDecision: EppDecision,
        internalNotes: string,
        notes: string,
        covid19VaccinationStatus: Covid19VaccinationStatus,
        bookedDateOfFirstVaccination: moment.Moment,
        dateOfFirstVaccination: moment.Moment,
        dateOfSecondVaccination: moment.Moment) => Promise<void>;
    onClose: () => void;
    evidenceOfCovid19VaccinationStatusMandatory?: boolean;
    covidVaccinationEvidenceSharingConsentOnPpq?: boolean;
    covidVaccinationEvidenceSharingConsentOnImmunisationReview?: boolean;
}

const ImmunisationClearModal: React.FunctionComponent<Props> = (props) => {
    const [eppDecision, setEppDecision] = React.useState<EppDecision>();
    const [notes, setNotes] = React.useState<string>();
    const [internalNotes, setInternalNotes] = React.useState<string>();
    const updateNotes = (snippetText: string) => {
        setNotes(prevNotes => prevNotes + " " + snippetText);
    };
    const updateInternalNotes = (snippetText: string) => {
        setInternalNotes(prevNotes => prevNotes + " " + snippetText);
    };
    const [confirmationShown, setconfirmationShown] = React.useState<boolean>(false);
    const [showErrors, setShowErrors] = React.useState(false);
    const [covid19VaccinationStatus, setCovid19VaccinationStatus] = React.useState<Covid19VaccinationStatus>();
    const [bookedDateOfFirstVaccination, setBookedDateOfFirstVaccination] = React.useState<moment.Moment>();
    const [dateOfFirstVaccination, setDateOfFirstVaccination] = React.useState<moment.Moment>();
    const [dateOfSecondVaccination, setDateOfSecondVaccination] = React.useState<moment.Moment>();

    const [showWarningModal, setShowWarningModal] = React.useState(false);

    const closeModal = () => setShowWarningModal(false);

    const openModal = () => setShowWarningModal(true);

    const onSubmit = async () => {
        setShowErrors(true);
        if (!eppDecision ||
            !notes ||
            (props.evidenceOfCovid19VaccinationStatusMandatory && !covid19VaccinationStatus) ||
            (covid19VaccinationStatus === Covid19VaccinationStatus.ClearedWith2Covid19Vaccinations &&
                !dateOfSecondVaccination) ||
            (covid19VaccinationStatus === Covid19VaccinationStatus.PartiallyCovid19Vaccinated &&
                !dateOfFirstVaccination) ||
            (covid19VaccinationStatus === Covid19VaccinationStatus.EmployeeNotCovid19VaccinatedButBookedFor1stVaccine &&
                !bookedDateOfFirstVaccination)) {
            return;
        }

        if (props.evidenceOfCovid19VaccinationStatusMandatory &&
            !props.covidVaccinationEvidenceSharingConsentOnPpq &&
            !props.covidVaccinationEvidenceSharingConsentOnImmunisationReview &&
            !confirmationShown) {
            openModal();
            setconfirmationShown(true);
            return;
        }

        await props.onSubmit(
            eppDecision,
            internalNotes,
            notes,
            covid19VaccinationStatus,
            bookedDateOfFirstVaccination,
            dateOfFirstVaccination,
            dateOfSecondVaccination);
        props.onClose();
    };

    const onEppDecisionChanged = (value: EppDecision) =>
        setEppDecision(value);

    const onCovid19VaccinationStatusChanged = (value: Covid19VaccinationStatus) =>
        setCovid19VaccinationStatus(value);

    const onDateOfFirstVaccinationChanged = (value: moment.Moment) =>
        setDateOfFirstVaccination(value);

    const onDateOfSecondVaccinationChanged = (value: moment.Moment) =>
        setDateOfSecondVaccination(value);

    const onBookedDateOfFirstVaccinationChanged = (value: moment.Moment) =>
        setBookedDateOfFirstVaccination(value);

    return (
        <>
            <Modal open>
                <Modal.Content>
                    <Form>
                        <Input.Dropdown
                            value={eppDecision}
                            label="EPP Decision"
                            showErrors={showErrors}
                            required
                            onChange={onEppDecisionChanged}
                            options={EppDecisions}
                        />
                        {props.evidenceOfCovid19VaccinationStatusMandatory &&
                            <>
                                <Input.Dropdown
                                    value={covid19VaccinationStatus}
                                    label="COVID-19 Vaccination Status"
                                    showErrors={showErrors}
                                    required
                                    onChange={onCovid19VaccinationStatusChanged}
                                    options={Covid19VaccinationStatuses}
                                />
                                {covid19VaccinationStatus === Covid19VaccinationStatus.PartiallyCovid19Vaccinated &&
                                    <Input.Date
                                        value={dateOfFirstVaccination}
                                        label="Date of first Vaccination"
                                        showErrors={showErrors}
                                        required
                                        onChange={onDateOfFirstVaccinationChanged}
                                    />
                                }
                                {covid19VaccinationStatus === Covid19VaccinationStatus.ClearedWith2Covid19Vaccinations &&
                                    <Input.Date
                                        value={dateOfSecondVaccination}
                                        label="Date of second Vaccination"
                                        showErrors={showErrors}
                                        required
                                        onChange={onDateOfSecondVaccinationChanged}
                                    />
                                }
                                {covid19VaccinationStatus === Covid19VaccinationStatus.EmployeeNotCovid19VaccinatedButBookedFor1stVaccine &&
                                    <Input.Date
                                        value={bookedDateOfFirstVaccination}
                                        label="Date first vaccine booked / planned for"
                                        showErrors={showErrors}
                                        required
                                        onChange={onBookedDateOfFirstVaccinationChanged}
                                    />
                                }
                            </>
                        }
                        <div className="text-snippet-container-padded">
                            <TextSnippetSelector onSelect={updateInternalNotes} />
                        </div>
                        <Input.Textarea
                            value={internalNotes}
                            label="Internal Notes"
                            showErrors={showErrors}
                            onChange={setInternalNotes}
                        />
                        <div className="text-snippet-container-padded">
                            <TextSnippetSelector onSelect={updateNotes} />
                        </div>
                        <Input.Textarea
                            value={notes}
                            label="Clinician Comments (These comments will be viewable on the immunisation clearance certificate)"
                            showErrors={showErrors}
                            required
                            onChange={setNotes}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <IsLoadingButton requestNames={MARK_OH_CASE_IMMUNISATION_CLEAR} content="Save" onClick={onSubmit} />
                </Modal.Actions>
            </Modal>
            {showWarningModal && <ImmunisationWarningModal onClose={closeModal} />}
        </>
    );
};

export { ImmunisationClearModal };
